/*
 * Cart drawer line upsells.
 *
 * Wires up the controls rendered by snippets/cart-drawer-line-upsell.liquid:
 * the volume-discount tier buttons, the "upgrade to subscription" button and
 * the delivery frequency select. Listeners are delegated from the document
 * because the drawer replaces its own innerHTML on every cart update.
 */
(function () {
  const BUSY_CLASS = 'cart-drawer__upsell--busy';

  function cartItemsElement() {
    return document.querySelector('cart-drawer-items') || document.querySelector('cart-items');
  }

  /*
   * Tier buttons are rendered by Liquid from the store-wide theme settings,
   * which cannot know which A/B group the shopper was assigned to. Rebuild them
   * from window.FBDiscountConfig, the same source the bundle builder prices
   * from, so the cart never advertises a different ladder than the PDP.
   *
   * Mirrors the Liquid: skip tiers already reached, never offer the best tier
   * as a second choice, and stop at the configured button limit.
   */
  function rebuildTierRows(scope) {
    const cfg = window.FBDiscountConfig;
    if (!cfg || typeof cfg.getTiers !== 'function') return;

    const tiers = cfg.getTiers()
      .filter((t) => t && t.minQty > 1 && t.discount > 0)
      .sort((a, b) => a.minQty - b.minQty);
    if (!tiers.length) return;

    const topTier = tiers[tiers.length - 1].minQty;
    const rows = (scope || document).querySelectorAll('[data-cart-tier-row]');

    rows.forEach((row) => {
      const cartQty = parseInt(row.dataset.cartQty, 10) || 0;
      const itemQty = parseInt(row.dataset.itemQty, 10) || 0;
      const maxButtons = parseInt(row.dataset.maxButtons, 10) || 2;
      const template = row.dataset.tierLabel || 'Buy [quantity] save [discount]%';

      const buttons = [];
      for (let i = 0; i < tiers.length; i += 1) {
        const tier = tiers[i];
        if (tier.minQty <= cartQty) continue;
        if (buttons.length > 0 && tier.minQty === topTier) continue;
        if (buttons.length >= maxButtons) break;

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'cart-drawer__tier-btn';
        button.setAttribute('data-cart-tier-btn', '');
        button.dataset.line = row.dataset.line || '';
        button.dataset.key = row.dataset.key || '';
        button.dataset.quantity = String(itemQty + (tier.minQty - cartQty));
        // textContent, not innerHTML: the label is merchant-editable copy.
        button.textContent = template
          .replace('[quantity]', tier.minQty)
          .replace('[discount]', tier.discount);
        buttons.push(button);
      }

      row.replaceChildren(...buttons);
      row.hidden = buttons.length === 0;
    });
  }

  // The drawer swaps its own innerHTML on every cart update, so re-apply after.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => rebuildTierRows());
  }
  // Fired when the active variant resolves, which can land after first paint.
  document.addEventListener('forwardbar:discount-variant-changed', () => rebuildTierRows());
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => rebuildTierRows());
  } else {
    rebuildTierRows();
  }

  function setBusy(control, busy) {
    control.classList.toggle(BUSY_CLASS, busy);
    control.disabled = busy;
  }

  function showError(message) {
    const errors = document.getElementById('CartDrawer-CartErrors') || document.getElementById('cart-errors');
    if (errors) errors.textContent = message;
  }

  /* Re-render first, then write the message: the error node sits inside the
     markup onCartUpdate replaces, so anything written before it is discarded. */
  function refreshAndWarn(cartItems) {
    const message =
      (window.cartStrings && window.cartStrings.staleCart) || 'Your cart has changed. Please try again.';
    const done = typeof cartItems.onCartUpdate === 'function' ? cartItems.onCartUpdate() : null;
    Promise.resolve(done).then(() => showError(message));
  }

  /* Mirrors the section swap CartItems.updateQuantity does, so a selling plan
     change refreshes exactly the same regions as a quantity change. */
  function renderSections(cartItems, parsedState) {
    cartItems.getSectionsToRender().forEach((section) => {
      const sectionHtml = parsedState.sections && parsedState.sections[section.section];
      if (sectionHtml == null) return;
      const host = document.getElementById(section.id);
      if (!host) return;
      const target = host.querySelector(section.selector) || host;
      const inner = cartItems.getSectionInnerHTML(sectionHtml, section.selector);
      if (inner !== '') target.innerHTML = inner;
    });
  }

  /* The rendered `data-line` is a 1-based index into the cart as it stood when
     the drawer was rendered, and the cart drifts out from under it — a checkout
     app adding or dropping its own hidden line, another tab, or iOS restoring
     the page from bfcache after a Back out of checkout. Every other cart call
     here sends the line item key instead, but /cart/change.js refuses `id` when
     a selling plan is involved, so this one path has to re-derive the index
     against the live cart. resolveCartLine (cart.js) does that, and copes with
     the key itself having been rehashed by a discount tier change.
     Resolves to null when the line can no longer be identified. */
  function resolveLine(cartItems, control, fallbackLine) {
    const key = control.dataset.key;
    if (!key || typeof resolveCartLine !== 'function' || typeof cartItems.lineIdentity !== 'function') {
      return Promise.resolve(fallbackLine);
    }
    const identity = cartItems.lineIdentity(fallbackLine, key);
    return resolveCartLine(identity).then((resolved) => (resolved ? resolved.line : null));
  }

  /* /cart/change.js only accepts `line` when a selling plan is involved, and
     wants the quantity restated, so both always go over the wire together. */
  function changeSellingPlan(control, line, quantity, sellingPlan) {
    const cartItems = cartItemsElement();
    if (!cartItems) return;

    setBusy(control, true);
    if (typeof cartItems.enableLoading === 'function') cartItems.enableLoading(line);

    /* The API documents the plan as an ID, so send a number rather than the
       string the dataset/select hands us. An empty value is the "one-time
       purchase" choice and has to travel as null to drop the plan — Number('')
       is 0, which would be sent as a plan id. */
    const raw = sellingPlan == null ? '' : String(sellingPlan).trim();
    const planId = raw === '' ? null : Number(raw);
    const sellingPlanValue = planId !== null && Number.isFinite(planId) && planId > 0 ? planId : null;

    resolveLine(cartItems, control, line)
      .then((liveLine) => {
        /* The line this control belongs to is no longer in the cart, so there
           is no index that could be right. Re-render rather than guess. */
        if (liveLine == null) {
          refreshAndWarn(cartItems);
          return null;
        }

        const body = JSON.stringify({
          line: liveLine,
          quantity: quantity,
          selling_plan: sellingPlanValue,
          sections: cartItems.getSectionsToRender().map((section) => section.section),
          sections_url: cartItems.tagName === 'CART-DRAWER-ITEMS' ? routes.cart_url : window.location.pathname,
        });

        return fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } }).then((response) =>
          response.text()
        );
      })
      .then((state) => {
        if (state == null) return;
        const parsedState = JSON.parse(state);

        /* A rejection comes back as a bare error object with no `items`. Its
           wording is aimed at developers, so show the customer the reason that
           actually applies to them and refresh the drawer they are looking at. */
        if (!parsedState.items) {
          refreshAndWarn(cartItems);
          return;
        }

        if (parsedState.errors) {
          showError(parsedState.errors);
          return;
        }

        showError('');
        cartItems.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) cartDrawer.classList.toggle('is-empty', parsedState.item_count === 0);

        renderSections(cartItems, parsedState);

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer-upsell', cartData: parsedState });
        }
      })
      .catch(() => {
        showError(window.cartStrings ? window.cartStrings.error : '');
      })
      .finally(() => {
        setBusy(control, false);
        if (typeof cartItems.disableLoading === 'function') cartItems.disableLoading(line);
      });
  }

  document.addEventListener('click', (event) => {
    const tierButton = event.target.closest('[data-cart-tier-btn]');
    if (tierButton) {
      event.preventDefault();
      const cartItems = cartItemsElement();
      if (!cartItems) return;
      const line = tierButton.dataset.line;
      const quantity = parseInt(tierButton.dataset.quantity, 10);

      /* updateQuantity decides whether Shopify clamped the request by comparing
         the new cart quantity against this input's value, so the input has to
         hold the requested quantity first — the theme's own +/- buttons set it
         before firing their change event. Leaving it stale makes a successful
         update look like an inventory cap. Set the property only, never the
         attribute: QuantityInput does the same, and cart.js reverts to the
         attribute when the cart genuinely rejects the quantity. */
      const input =
        document.getElementById(`Drawer-quantity-${line}`) || document.getElementById(`Quantity-${line}`);
      if (input) input.value = String(quantity);

      /* No busy state of our own here: updateQuantity's enableLoading already
         freezes the whole item list and shows the line spinner, and its finally
         clears that on success and failure alike. Disabling this button instead
         would strand it, because a failed update leaves the DOM in place rather
         than re-rendering the section that would replace the button. */
      cartItems.updateQuantity(line, quantity, event, undefined, undefined, tierButton.dataset.key);
      return;
    }

    const upgradeButton = event.target.closest('[data-cart-sub-upgrade]');
    if (upgradeButton) {
      event.preventDefault();
      changeSellingPlan(
        upgradeButton,
        upgradeButton.dataset.line,
        parseInt(upgradeButton.dataset.quantity, 10),
        upgradeButton.dataset.sellingPlan
      );
    }
  });

  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-cart-freq-select]');
    if (!select) return;
    changeSellingPlan(select, select.dataset.line, parseInt(select.dataset.quantity, 10), select.value);
  });
})();
